import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';

import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';

import Auth from '../../../../data/auth-service';
import { Protocols } from '../../../../data/filter-service';

import Validator, {
    useValidate,
    checkValidity,
} from '../../../../components/validator';

const useStyles = makeStyles((theme) => ({
    inputField: {
        width: '100%',
    },
}));

const RuleData = (props) => {
    const classes = useStyles();
    const [isUpdate, setIsUpdate] = useState(!!props.ruleId);
    const [changes, setChanges] = useState(false);
    const [rule, setRule] = useState({
        TrafficMirrorFilterRuleId: '',
        TrafficMirrorFilterId: '',
        TrafficDirection: 'egress || ingress',
        RuleNumber: 0,
        RuleAction: '',
        Protocol: -1,
        DestinationPortRange: {
            FromPort: 0,
            ToPort: 0,
        },
        SourcePortRange: {
            FromPort: 0,
            ToPort: 0,
        },
        DestinationCidrBlock: '',
        SourceCidrBlock: '',
        Description: '',
    });

    const ruleForm = useValidate({});
    const [valid, setValid] = useState(false);
    const [original, setOriginal] = useState();
    const awsPerms = Auth.permissions.get('AWS');
    const [readonly] = useState(!awsPerms.write);

    useEffect(() => {
        setRule(_cloneDeep(props.rule));
        setOriginal(_cloneDeep(props.rule));
        setIsUpdate(!!props.ruleId);
    }, [props.rule, props.ruleId]);

    useEffect(() => {
        setValid(
            (rule.SourceCidrBlock || '').length > 0 &&
            (rule.DestinationCidrBlock || '').length > 0 &&
            checkValidity(ruleForm.get())
        );
        setChanges(!_isEqual(original, rule));
    }, [rule, original, ruleForm]);

    const deleteRule = () => {
        props.handleDelete(props.index, rule.RuleNumber);
    };

    const saveRule = () => {
        props.handleCreateOrModify(rule, isUpdate, props.index);
    };

    const handleRuleNumber = (e, validID, valid) => {
        ruleForm.set({ ...ruleForm.get(), [validID]: valid });
        const r = { ...rule };
        r.RuleNumber = e.target.value;
        setRule(r);
    };

    const handleRuleAction = (e, component) => {
        const r = { ...rule };
        r.RuleAction = component.props.value;
        setRule(r);
    };

    const handleSourcePort = (e, validID, valid) => {
        ruleForm.set({ ...ruleForm.get(), [validID]: valid });
        const r = { ...rule };

        if (!r.SourcePortRange) {
            r.SourcePortRange = {
                FromPort: 0,
                ToPort: 0,
            };
        }
        const portValue = parseInt(e.target.value);
        r.SourcePortRange.FromPort = portValue;
        r.SourcePortRange.ToPort = portValue;
        setRule(r);
    };

    const handleDestinationPort = (e, validID, valid) => {
        ruleForm.set({ ...ruleForm.get(), [validID]: valid });
        const r = { ...rule };

        if (!r.DestinationPortRange) {
            r.DestinationPortRange = {
                FromPort: 0,
                ToPort: 0,
            };
        }
        const portValue = parseInt(e.target.value);
        r.DestinationPortRange.FromPort = portValue;
        r.DestinationPortRange.ToPort = portValue;
        setRule(r);
    };
    const handleSourceCidr = (e, validID, valid) => {
        ruleForm.set({ ...ruleForm.get(), [validID]: valid });
        const r = { ...rule };
        r.SourceCidrBlock = e.target.value;
        setRule(r);
    };

    const handleDestinationCidr = (e, validID, valid) => {
        ruleForm.set({ ...ruleForm.get(), [validID]: valid });
        const r = { ...rule };
        r.DestinationCidrBlock = e.target.value;
        setRule(r);
    };

    const handleProtocol = (e, component) => {
        const r = { ...rule };
        r.Protocol = component.props.value;
        setRule(r);
    };

    return (
        <Grid container justifyContent="space-between">
            <Grid item xs={1}>
                <Validator
                    disabled={readonly}
                    className={classes.inputField}
                    variant="standard"
                    valueType="range"
                    errorText="RuleNumber max must be within 0 and 32766"
                    fullWidth
                    type="number"
                    inputProps={{
                        min: 0,
                        max: 32766,
                    }}
                    name="RuleNumber"
                    value={rule.RuleNumber}
                    onChange={(e, validID, valid) => {
                        handleRuleNumber(e, validID, valid);
                    }}
                />
            </Grid>
            <Grid item xs={1}>
                <Select
                    disabled={readonly}
                    className={classes.inputField}
                    variant="standard"
                    name="RuleAction"
                    value={rule.RuleAction}
                    onChange={handleRuleAction}
                >
                    <MenuItem value="accept">accept</MenuItem>
                    <MenuItem value="reject">reject</MenuItem>
                </Select>
            </Grid>
            <Grid item xs={1}>
                <Select
                    disabled={readonly}
                    className={classes.inputField}
                    variant="standard"
                    name="Protocol"
                    value={rule.Protocol !== undefined ? rule.Protocol : -1}
                    onChange={handleProtocol}
                >
                    {Object.keys(Protocols).map((protocolName, index) => (
                        <MenuItem
                            key={'protocol-' + index}
                            value={Protocols[protocolName].number}
                        >
                            {Protocols[protocolName].keyword}
                        </MenuItem>
                    ))}
                </Select>
            </Grid>
            <Grid item xs={1}>
                <Validator
                    className={classes.inputField}
                    variant="standard"
                    valueType="range"
                    errorText="Please enter a valid source port range."
                    fullWidth
                    type="number"
                    inputProps={{
                        min: 0,
                        max: 65535,
                    }}
                    name="SourcePortRange"
                    value={
                        rule.Protocol !== 6 && rule.Protocol !== 17
                            ? 0
                            : rule.SourcePortRange
                                ? rule.SourcePortRange.FromPort
                                : 0
                    }
                    onChange={handleSourcePort}
                    disabled={readonly || (rule.Protocol !== 6 && rule.Protocol !== 17)}
                />
            </Grid>
            <Grid item xs={1}>
                <Validator
                    className={classes.inputField}
                    variant="standard"
                    valueType="range"
                    errorText="Please enter a valid destination port range."
                    fullWidth
                    type="number"
                    inputProps={{
                        min: 0,
                        max: 65535,
                    }}
                    name="DestinationPortRange"
                    value={
                        rule.Protocol !== 6 && rule.Protocol !== 17
                            ? 0
                            : rule.DestinationPortRange
                                ? rule.DestinationPortRange.FromPort
                                : 0
                    }
                    onChange={handleDestinationPort}
                    disabled={readonly || (rule.Protocol !== 6 && rule.Protocol !== 17)}
                />
            </Grid>
            <Grid item xs={1}>
                <Validator
                    disabled={readonly}
                    className={classes.inputField}
                    valueType="cidr"
                    variant="standard"
                    errorText="An IPv4 or IPv6 CIDR range is required"
                    fullWidth
                    name="SourceCidrBlock"
                    value={rule.SourceCidrBlock}
                    onChange={handleSourceCidr}
                />
            </Grid>
            <Grid item xs={1}>
                <Validator
                    disabled={readonly}
                    className={classes.inputField}
                    valueType="cidr"
                    variant="standard"
                    errorText="An IPv4 or IPv6 CIDR range is required"
                    fullWidth
                    name="DestinationCidrBlock"
                    value={rule.DestinationCidrBlock}
                    onChange={handleDestinationCidr}
                />
            </Grid>
            <Grid item xs={2}>
                {!readonly && <IconButton
                    aria-label="save"
                    name="save"
                    title="save"
                    disabled={!valid || !changes}
                    onClick={saveRule}
                    size="large"
                >
                    <SaveIcon />
                </IconButton>}
                {!readonly && <IconButton
                    aria-label="delete"
                    color="secondary"
                    name="delete"
                    title="delete"
                    onClick={deleteRule}
                    size="large"
                    disabled={props.loading}
                >
                    <DeleteIcon />
                </IconButton>}
            </Grid>
        </Grid>
    );
};

RuleData.propTypes = {
    handleDelete: PropTypes.func.isRequired,
    handleCreateOrModify: PropTypes.func.isRequired,
    ruleId: PropTypes.string,
    index: PropTypes.number,
    rule: PropTypes.shape({
        RuleNumber: PropTypes.number.isRequired,
        RuleAction: PropTypes.string.isRequired,
        Protocol: PropTypes.number,
        SourcePortRange: PropTypes.shape({
            FromPort: PropTypes.number,
            ToPort: PropTypes.number,
        }),
        DestinationPortRange: PropTypes.shape({
            FromPort: PropTypes.number,
            ToPort: PropTypes.number,
        }),
        SourceCidrBlock: PropTypes.string.isRequired,
        DestinationCidrBlock: PropTypes.string.isRequired,
    }),
};

export default RuleData;
